import APR_REPORT_AGETH from "@data/APR_REPORT_AGETH.json";
import APR_REPORT_HGETH from "@data/APR_REPORT_HGETH.json";
import APR_REPORT_SBUSD from "@data/APR_REPORT_SBUSD.json";

/**
 * Supported vault token types for strategy reports
 */
export type TokenType = "agETH" | "hgETH" | "sbUSD";

/**
 * Strategy report data structure
 */
interface StrategyReportData {
  allocations: Array<{
    apy?: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

/**
 * Mapping of token types to their corresponding strategy report JSON data
 */
const STRATEGY_REPORTS_MAP: Record<TokenType, StrategyReportData> = {
  agETH: APR_REPORT_AGETH as StrategyReportData,
  hgETH: APR_REPORT_HGETH as StrategyReportData,
  sbUSD: APR_REPORT_SBUSD as StrategyReportData,
};

/**
 * Get the latest avgAPY from strategy reports JSON files
 * Returns the avgAPY from the first (latest) allocation in the allocations array
 *
 * @param vaultType - The vault token type (agETH, hgETH, or sbUSD)
 * @returns The latest avgAPY as a number, or null if not available
 *
 * @example
 * getLatestAvgApyFromReports("agETH")
 * // Returns: 8.33 (or null if not available)
 */
export const getLatestAvgApyFromReports = (
  vaultType: TokenType
): number | null => {
  const reportData = STRATEGY_REPORTS_MAP[vaultType];

  if (!reportData) {
    return null;
  }

  const allocations = reportData?.allocations || [];
  if (allocations.length === 0) {
    return null;
  }

  // Get the latest allocation (first item in the array)
  // Note: The allocations array is typically ordered with the latest first
  const latestAllocation = allocations[0];
  const avgApy = latestAllocation?.apy;

  // Validate and convert to number
  if (avgApy && !isNaN(+avgApy)) {
    return +avgApy;
  }

  return null;
};
